import { Component, OnDestroy, OnInit } from "@angular/core";
import { Observable } from "rxjs/Observable";
import { Router, ActivatedRoute } from "@angular/router";

import { SessionManagerService } from "../../_services/SessionManager/session-manager.service";
import { AuthenticationService } from "../../_services/authentication.service";
import { environment } from "../../../environments/environment";
import { AlertService } from "../../_services/alert.service";
import {
  SecondFactorChallenge,
  SecondFactorChannel,
} from "../../_models/second-factor";

/** The server lets a new code be asked for only after this long */
const RESEND_WAIT_SECONDS = 60;

/**
 * The two-step login (password, then a code), routed to while the `twoFactorLogin` flag is on
 *
 * TODO: Review of the Android Enter-key fix (PR 71) says the root cause is a poor use of an HTML <form>, which,
 * in the reviewer's opinion, should not have been here in the first place. The proper fix is to remove the <form>
 * elements where they are not needed.
 * A comprehensive fix is needed later: decide whether the <form> should be removed or not, and how to handle it.
 * Things to weigh: a <form> brings implicit submission on Enter (the cause of the bug) and a hidden "default button",
 * but it is also what password managers, browser autofill and accessibility rely on in a login screen, and without it
 * Enter has to be handled by hand (the old keyCode 13 handler, which Android keyboards bypass with keyCode 229).
 * For now the fix is only `type="button"` on the "new user" button and no key handler, so Enter submits through ngSubmit.
 */
@Component({
  moduleId: module.id,
  templateUrl: "2fa-login.component.html",
  styleUrls: ["2fa-login.component.scss"],
})
export class TwoFactorLoginComponent implements OnInit, OnDestroy {
  model: any = {};
  loading = false;
  returnUrl: string;
  isPasswordVisible = false;
  step: "credentials" | "code" = "credentials";
  challenge: SecondFactorChallenge;
  code = "";
  rememberDevice = false;
  resendIn = 0;
  useCodeCubes = !!(
    environment.features && environment.features.twoFactorCodeCubes
  );
  noContact = false;
  rememberDeviceDays$: Observable<number> =
    this.authenticationService.rememberDeviceDays$;
  private resendTimer: any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authenticationService: AuthenticationService,
    private alertService: AlertService,
    private sessionManager: SessionManagerService
  ) { }

  ngOnInit() {
    localStorage.removeItem("allowShowPopup");
    this.returnUrl = this.route.snapshot.queryParams["returnUrl"] || "/";
  }

  ngOnDestroy() {
    clearTimeout(this.resendTimer);
  }

  forgotPassword() {
    this.router.navigate(["/forgotpassword"]);
  }

  toggleVisibility(event: MouseEvent) {
    event.preventDefault();
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  login() {
    this.loading = true;
    this.noContact = false;

    this.authenticationService
      .loginWithTwoFactor(this.model.username, this.model.password)
      .subscribe(
        (data) => {
          if (data.status === "SECOND_FACTOR_REQUIRED") {
            this.showCodeStep(data.challenge);
            return;
          }
          this.finishLogin();
        },
        (error) => this.showError(error)
      );
  }

  verifyCode() {
    this.loading = true;

    this.authenticationService
      .verifyCode(this.code.trim(), this.rememberDevice)
      .subscribe(
        () => this.finishLogin(),
        (error) => {
          const body = this.showError(error);
          if (body && this.endsTheLogin(body.code)) {
            this.backToLogin();
          }
        }
      );
  }

  /** Asks for a new code, by email if the user chose that and by the channel of the last code otherwise */
  resendCode(channel?: SecondFactorChannel) {
    if (channel === "EMAIL" && !this.challenge.canUseEmail) {
      return;
    }
    this.loading = true;

    this.authenticationService.resendCode(channel).subscribe(
      (challenge) => {
        this.showCodeStep(challenge);
        this.alertService.success("נשלח קוד חדש");
      },
      (error) => {
        const body = this.showError(error);
        if (body && body.code === "RESEND_TOO_SOON") {
          this.startResendCountdown(body.retryAfterSeconds);
        } else if (body && this.endsTheLogin(body.code)) {
          this.backToLogin();
        }
      }
    );
  }

  backToLogin() {
    clearTimeout(this.resendTimer);
    this.resendIn = 0;
    this.step = "credentials";
    this.challenge = undefined;
    this.code = "";
    this.model.password = "";
    this.loading = false;
  }

  private showCodeStep(challenge: SecondFactorChallenge) {
    this.challenge = challenge;
    this.code = "";
    this.step = "code";
    this.loading = false;
    this.startResendCountdown(RESEND_WAIT_SECONDS);
  }

  private startResendCountdown(seconds: number) {
    clearTimeout(this.resendTimer);
    this.resendIn = seconds || RESEND_WAIT_SECONDS;
    this.countDown();
  }

  private countDown() {
    if (this.resendIn > 0) {
      this.resendTimer = setTimeout(() => {
        this.resendIn--;
        this.countDown();
      }, 1000);
    }
  }

  private finishLogin() {
    this.sessionManager.recordUserLoginTime();
    this.router.navigate([this.returnUrl]);
  }

  /** A code that is locked, expired or has no login waiting for it cannot be fixed on the code screen */
  private endsTheLogin(code: string): boolean {
    return (
      ["NO_PENDING_LOGIN", "CODE_LOCKED", "CODE_EXPIRED"].indexOf(code) > -1
    );
  }

  /** Shows what the server said and returns it, so the caller can tell what went wrong */
  private showError(error): any {
    let body;
    if (error && error.status !== 200) {
      if (error.status === 504 || error.status === 502) {
        this.alertService.error("שגיאה בהתחברות לשרת");
      } else {
        try {
          body = error.json && error.json();
          if (body && body.code === "NO_CONTACT") {
            // the message and the way to get help are on the screen, not in an alert
            this.noContact = true;
          } else {
            this.alertService.error(
              this.errorMessage(body) || error.statusText
            );
          }
        } catch (err) {
          this.alertService.error(err);
        }
      }
      this.loading = false;
    }
    return body;
  }

  private errorMessage(body): string {
    if (!body || !body.statusText) {
      return undefined;
    }
    return body.attemptsLeft === undefined
      ? body.statusText
      : `${body.statusText} (נותרו ${body.attemptsLeft} ניסיונות)`;
  }
}



// WEBPACK FOOTER //
// ./src/app/login/2fa-login/2fa-login.component.ts